"use client";

import { c as _c } from "react/compiler-runtime";
import { useAuth } from "@/components/auth/Auth";
import { useNotificationsContext } from "@/components/notifications/NotificationsContext";
import WaveDropsScrollingOverlay from "@/components/waves/drops/WaveDropsScrollingOverlay";
import { UnreadDividerProvider, useUnreadDivider } from "@/contexts/wave/UnreadDividerContext";
import { useWaveChatScrollOptional } from "@/contexts/wave/WaveChatScrollContext";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import type { Drop, ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { DropSize } from "@/helpers/waves/drop.helpers";
import { isWaveDirectMessage } from "@/helpers/waves/wave.helpers";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { useScrollBehavior } from "@/hooks/useScrollBehavior";
import { useVirtualizedWaveDrops } from "@/hooks/useVirtualizedWaveDrops";
import { useWaveBoostedDrops } from "@/hooks/useWaveBoostedDrops";
import { useWaveIsTyping } from "@/hooks/useWaveIsTyping";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { TweetPreviewModeProvider, type TweetPreviewMode } from "@/components/tweets/TweetPreviewModeContext";
import { useWaveDropsClipboard } from "./hooks/useWaveDropsClipboard";
import { useDeferredNewestDrops } from "./hooks/useDeferredNewestDrops";
import { useWaveDropsNotificationRead } from "./hooks/useWaveDropsNotificationRead";
import { useWaveDropsSerialScroll } from "./hooks/useWaveDropsSerialScroll";
import { WaveDropsContent } from "./subcomponents/WaveDropsContent";
const EMPTY_DROPS: Drop[] = [];
interface WaveDropsAllProps {
  readonly waveId: string;
  readonly dropId: string | null;
  readonly onReply: ({
    drop,
    partId
  }: {
    drop: ApiDrop;
    partId: number;
  }) => void;
  readonly activeDrop: ActiveDropState | null;
  readonly initialDrop: number | null;
  readonly dividerSerialNo?: number | null | undefined;
  readonly unreadCount?: number | undefined;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
  readonly bottomPaddingClassName?: string | undefined;
  readonly isMuted?: boolean | undefined;
}
const WaveDropsAllInner: React.FC<WaveDropsAllProps> = t0 => {
  const $ = _c(84);
  const {
    waveId,
    dropId,
    onReply,
    activeDrop,
    initialDrop,
    dividerSerialNo,
    unreadCount,
    onDropContentClick,
    bottomPaddingClassName,
    isMuted: t1
  } = t0;
  const isMuted = t1 === undefined ? false : t1;
  const router = useRouter();
  const {
    removeWaveDeliveredNotifications
  } = useNotificationsContext();
  const {
    connectedProfile
  } = useAuth();
  const {
    isAppleMobile
  } = useDeviceInfo();
  const containerRef = useRef(null);
  const {
    waveMessages,
    fetchNextPage,
    waitAndRevealDrop
  } = useVirtualizedWaveDrops(waveId, dropId);
  const {
    setUnreadDividerSerialNo
  } = useUnreadDivider();
  const typingMessage = useWaveIsTyping(waveId, connectedProfile?.handle ?? null, isMuted);
  let t2;
  if ($[0] !== waveId) {
    t2 = {
      waveId
    };
    $[0] = waveId;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    data: boostedDrops
  } = useWaveBoostedDrops(t2);
  const scrollBehavior = useScrollBehavior();
  const {
    scrollContainerRef,
    scrollContainerCallbackRef,
    bottomAnchorRef,
    bottomAnchorCallbackRef,
    isAtBottom,
    shouldPinToBottom,
    scrollToVisualBottom,
    forcePinToBottom
  } = scrollBehavior;
  let t3;
  if ($[2] !== removeWaveDeliveredNotifications || $[3] !== waveId) {
    t3 = {
      waveId,
      removeWaveDeliveredNotifications
    };
    $[2] = removeWaveDeliveredNotifications;
    $[3] = waveId;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  useWaveDropsNotificationRead(t3);
  waveMessages?.drops;
  const dropsForClipboard = waveMessages?.drops ?? EMPTY_DROPS;
  let t4;
  if ($[5] !== dropsForClipboard) {
    t4 = {
      containerRef,
      drops: dropsForClipboard
    };
    $[5] = dropsForClipboard;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  useWaveDropsClipboard(t4);
  const initializedWaveRef = useRef(null);
  let t5;
  let t6;
  if ($[7] !== dividerSerialNo || $[8] !== setUnreadDividerSerialNo || $[9] !== waveId) {
    t5 = () => {
      if (initializedWaveRef.current === waveId) {
        return;
      }
      initializedWaveRef.current = waveId;
      setUnreadDividerSerialNo(dividerSerialNo ?? null);
    };
    t6 = [waveId, dividerSerialNo, setUnreadDividerSerialNo];
    $[7] = dividerSerialNo;
    $[8] = setUnreadDividerSerialNo;
    $[9] = waveId;
    $[10] = t5;
    $[11] = t6;
  } else {
    t5 = $[10];
    t6 = $[11];
  }
  useEffect(t5, t6);
  let t7;
  if ($[12] !== isAppleMobile || $[13] !== shouldPinToBottom || $[14] !== waveId || $[15] !== waveMessages) {
    t7 = {
      waveId,
      isAppleMobile,
      waveMessages,
      shouldPinToBottom
    };
    $[12] = isAppleMobile;
    $[13] = shouldPinToBottom;
    $[14] = waveId;
    $[15] = waveMessages;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const {
    renderedWaveMessages,
    pendingDropsCount,
    revealPendingDrops: revealDeferredPendingDrops
  } = useDeferredNewestDrops(t7);
  let t8;
  if ($[17] !== dropId || $[18] !== fetchNextPage || $[19] !== initialDrop || $[20] !== renderedWaveMessages || $[21] !== scrollContainerRef || $[22] !== scrollToVisualBottom || $[23] !== shouldPinToBottom || $[24] !== waitAndRevealDrop || $[25] !== waveId || $[26] !== waveMessages) {
    t8 = {
      waveId,
      dropId,
      initialDrop,
      waveMessages,
      renderedWaveMessages,
      fetchNextPage,
      waitAndRevealDrop,
      scrollContainerRef,
      shouldPinToBottom,
      scrollToVisualBottom
    };
    $[17] = dropId;
    $[18] = fetchNextPage;
    $[19] = initialDrop;
    $[20] = renderedWaveMessages;
    $[21] = scrollContainerRef;
    $[22] = scrollToVisualBottom;
    $[23] = shouldPinToBottom;
    $[24] = waitAndRevealDrop;
    $[25] = waveId;
    $[26] = waveMessages;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  const {
    serialTarget,
    queueSerialTarget,
    targetDropRef,
    isScrolling,
    scrollBaselineSerials,
    frozenAutoCollapseSerials
  } = useWaveDropsSerialScroll(t8);
  let t9;
  if ($[28] !== frozenAutoCollapseSerials || $[29] !== isScrolling || $[30] !== renderedWaveMessages?.drops || $[31] !== scrollBaselineSerials) {
    t9 = () => {
      if (!isScrolling || !scrollBaselineSerials) {
        return frozenAutoCollapseSerials;
      }
      const drops = renderedWaveMessages?.drops;
      if (!drops || drops.length === 0) {
        return frozenAutoCollapseSerials;
      }
      const next = new Set(frozenAutoCollapseSerials);
      for (const drop of drops) {
        const serialNo = drop.serial_no;
        if (typeof serialNo !== "number") {
          continue;
        }
        if (!scrollBaselineSerials.has(serialNo) && !next.has(serialNo)) {
          next.add(serialNo);
        }
      }
      return next;
    };
    $[28] = frozenAutoCollapseSerials;
    $[29] = isScrolling;
    $[30] = renderedWaveMessages?.drops;
    $[31] = scrollBaselineSerials;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  renderedWaveMessages?.drops;
  let t10;
  if ($[33] !== t9) {
    t10 = t9();
    $[33] = t9;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  const autoCollapseSerials = t10;
  const waveChatScroll = useWaveChatScrollOptional();
  let t11;
  let t12;
  if ($[35] !== queueSerialTarget || $[36] !== waveChatScroll || $[37] !== waveId) {
    t11 = () => {
      if (!waveChatScroll) {
        return;
      }
      return waveChatScroll.registerScrollHandler({
        waveId,
        handler: queueSerialTarget
      });
    };
    t12 = [waveChatScroll, waveId, queueSerialTarget];
    $[35] = queueSerialTarget;
    $[36] = waveChatScroll;
    $[37] = waveId;
    $[38] = t11;
    $[39] = t12;
  } else {
    t11 = $[38];
    t12 = $[39];
  }
  useEffect(t11, t12);
  let t13;
  if ($[40] !== forcePinToBottom || $[41] !== revealDeferredPendingDrops || $[42] !== waveMessages?.drops?.length) {
    t13 = () => {
      if (!waveMessages?.drops?.length) {
        return;
      }
      revealDeferredPendingDrops();
      forcePinToBottom();
    };
    $[40] = forcePinToBottom;
    $[41] = revealDeferredPendingDrops;
    $[42] = waveMessages?.drops?.length;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  const revealPendingDrops = t13;
  let t14;
  if ($[44] !== dropId || $[45] !== fetchNextPage || $[46] !== waveId || $[47] !== waveMessages?.hasNextPage || $[48] !== waveMessages?.isLoading || $[49] !== waveMessages?.isLoadingNextPage) {
    t14 = async () => {
      if (waveMessages?.hasNextPage && !waveMessages?.isLoading && !waveMessages?.isLoadingNextPage) {
        await fetchNextPage({
          waveId,
          type: DropSize.FULL
        }, dropId);
      }
    };
    $[44] = dropId;
    $[45] = fetchNextPage;
    $[46] = waveId;
    $[47] = waveMessages?.hasNextPage;
    $[48] = waveMessages?.isLoading;
    $[49] = waveMessages?.isLoadingNextPage;
    $[50] = t14;
  } else {
    t14 = $[50];
  }
  waveMessages?.hasNextPage;
  waveMessages?.isLoading;
  waveMessages?.isLoadingNextPage;
  const handleTopIntersection = t14;
  let t15;
  if ($[51] !== queueSerialTarget || $[52] !== router || $[53] !== waveId) {
    t15 = drop_0 => {
      if (drop_0.wave.id === waveId) {
        queueSerialTarget(drop_0.serial_no);
      } else {
        const waveDetails = drop_0.wave as unknown as {
          chat?: {
            scope?: {
              group?: {
                is_direct_message?: boolean | undefined;
              } | undefined;
            } | undefined;
          } | undefined;
        } ?? undefined;
        const isDirectMessage = isWaveDirectMessage(drop_0.wave.id, waveDetails);
        const href = getWaveRoute({
          waveId: drop_0.wave.id,
          serialNo: drop_0.serial_no,
          isDirectMessage,
          isApp: false
        });
        router.push(href);
      }
    };
    $[51] = queueSerialTarget;
    $[52] = router;
    $[53] = waveId;
    $[54] = t15;
  } else {
    t15 = $[54];
  }
  const handleQuoteClick = t15;
  let t16;
  if ($[55] !== activeDrop || $[56] !== autoCollapseSerials || $[57] !== boostedDrops || $[58] !== bottomAnchorCallbackRef || $[59] !== bottomAnchorRef || $[60] !== bottomPaddingClassName || $[61] !== dropId || $[62] !== forcePinToBottom || $[63] !== handleQuoteClick || $[64] !== handleTopIntersection || $[65] !== isAtBottom || $[66] !== onDropContentClick || $[67] !== onReply || $[68] !== pendingDropsCount || $[69] !== queueSerialTarget || $[70] !== renderedWaveMessages || $[71] !== revealPendingDrops || $[72] !== scrollContainerCallbackRef || $[73] !== scrollContainerRef || $[74] !== serialTarget || $[75] !== targetDropRef || $[76] !== typingMessage || $[77] !== unreadCount) {
    t16 = <TweetPreviewModeProvider mode="never"><WaveDropsContent waveMessages={renderedWaveMessages} dropId={dropId} scrollContainerRef={scrollContainerRef} scrollContainerCallbackRef={scrollContainerCallbackRef} bottomAnchorRef={bottomAnchorRef} bottomAnchorCallbackRef={bottomAnchorCallbackRef} onTopIntersection={handleTopIntersection} onReply={onReply} queueSerialTarget={queueSerialTarget} activeDrop={activeDrop} serialTarget={serialTarget} targetDropRef={targetDropRef} onQuoteClick={handleQuoteClick} isAtBottom={isAtBottom} scrollToBottom={forcePinToBottom} typingMessage={typingMessage} onDropContentClick={onDropContentClick} pendingCount={pendingDropsCount} onRevealPending={revealPendingDrops} bottomPaddingClassName={bottomPaddingClassName} boostedDrops={boostedDrops} onBoostedDropClick={queueSerialTarget} onScrollToUnread={queueSerialTarget} unreadCount={unreadCount} autoCollapseSerials={autoCollapseSerials} /></TweetPreviewModeProvider>;
    $[55] = activeDrop;
    $[56] = autoCollapseSerials;
    $[57] = boostedDrops;
    $[58] = bottomAnchorCallbackRef;
    $[59] = bottomAnchorRef;
    $[60] = bottomPaddingClassName;
    $[61] = dropId;
    $[62] = forcePinToBottom;
    $[63] = handleQuoteClick;
    $[64] = handleTopIntersection;
    $[65] = isAtBottom;
    $[66] = onDropContentClick;
    $[67] = onReply;
    $[68] = pendingDropsCount;
    $[69] = queueSerialTarget;
    $[70] = renderedWaveMessages;
    $[71] = revealPendingDrops;
    $[72] = scrollContainerCallbackRef;
    $[73] = scrollContainerRef;
    $[74] = serialTarget;
    $[75] = targetDropRef;
    $[76] = typingMessage;
    $[77] = unreadCount;
    $[78] = t16;
  } else {
    t16 = $[78];
  }
  let t17;
  if ($[79] !== isScrolling) {
    t17 = <WaveDropsScrollingOverlay isVisible={isScrolling} />;
    $[79] = isScrolling;
    $[80] = t17;
  } else {
    t17 = $[80];
  }
  let t18;
  if ($[81] !== t16 || $[82] !== t17) {
    t18 = <div ref={containerRef} className="tw-relative tw-flex tw-h-full tw-flex-col tw-justify-end tw-overflow-hidden tw-bg-iron-950">{t16}{t17}</div>;
    $[81] = t16;
    $[82] = t17;
    $[83] = t18;
  } else {
    t18 = $[83];
  }
  return t18;
};
export const WaveDropsAllWithoutProvider: React.FC<WaveDropsAllProps> = WaveDropsAllInner;
const WaveDropsAll: React.FC<WaveDropsAllProps> = t0 => {
  const $ = _c(15);
  const {
    waveId,
    dropId,
    onReply,
    activeDrop,
    initialDrop,
    dividerSerialNo,
    unreadCount,
    onDropContentClick,
    bottomPaddingClassName,
    isMuted: t1
  } = t0;
  const isMuted = t1 === undefined ? false : t1;
  const t2 = dividerSerialNo ?? null;
  const t3 = `unread-divider-${waveId}`;
  let t4;
  if ($[0] !== activeDrop || $[1] !== bottomPaddingClassName || $[2] !== dividerSerialNo || $[3] !== dropId || $[4] !== initialDrop || $[5] !== isMuted || $[6] !== onDropContentClick || $[7] !== onReply || $[8] !== unreadCount || $[9] !== waveId) {
    t4 = <WaveDropsAllInner key={waveId} waveId={waveId} dropId={dropId} onReply={onReply} activeDrop={activeDrop} initialDrop={initialDrop} dividerSerialNo={dividerSerialNo} unreadCount={unreadCount} onDropContentClick={onDropContentClick} bottomPaddingClassName={bottomPaddingClassName} isMuted={isMuted} />;
    $[0] = activeDrop;
    $[1] = bottomPaddingClassName;
    $[2] = dividerSerialNo;
    $[3] = dropId;
    $[4] = initialDrop;
    $[5] = isMuted;
    $[6] = onDropContentClick;
    $[7] = onReply;
    $[8] = unreadCount;
    $[9] = waveId;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t2 || $[12] !== t3 || $[13] !== t4) {
    t5 = <UnreadDividerProvider initialSerialNo={t2} key={t3}>{t4}</UnreadDividerProvider>;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
};
export default WaveDropsAll;